import { ActivityAttributeFilters } from '@/components/features/Activity/ActivityAttributeFilters';
import { Sticky } from '@/config/sticky-layout';
import { useMarketplaceChain } from '@/hooks/use-marketplace-chain';
import { getChainExplorerUrl } from '@/utils/chainExplorers';
import { downloadFile } from '@/utils/download';
import { getCSVDocumentString } from '@/utils/getCSV';
import { Box, Button, Checkbox, Divider, Flex, HStack, Heading, Stack } from '@chakra-ui/react';
import { formatTokenAmount } from '@onbeam/utils';
import { ExportIcon } from '@sphere/icons';
import { FilterDrawer } from '@sphere/ui';
import { formatISO } from 'date-fns';
import useTranslation from 'next-translate/useTranslation';
import { rem } from 'polished';
import { FC, Fragment, useCallback } from 'react';
import RefreshButton from '../Search/RefreshButton';
import {
  Activity,
  ActivityResponse,
  ActivityType,
  ActivityTypeFilterType,
  ActivityTypeFilters,
  getActivityEventTitle,
} from './utils';

type Props = {
  address: string;
  data: ActivityResponse['data'];
  selectedFilters: ActivityTypeFilterType[];
  refetch: () => void;
  isRefetching: boolean;
  onFilterToggle(type: ActivityTypeFilterType): void;
  title?: string;
  withRefreshButton?: boolean;
  withAttributeFilters?: boolean;
};

const ActivityFilters: FC<Props> = ({
  address,
  data,
  isRefetching,
  refetch,
  selectedFilters,
  onFilterToggle,
  title,
  withRefreshButton,
  withAttributeFilters,
}) => {
  const { t } = useTranslation('activity');
  const marketplaceChain = useMarketplaceChain();

  const handleDownload = useCallback(() => {
    const documentData = data.map(activity => ({
      Event: getActivityEventTitle(activity.type as ActivityType, t),
      Price: formatTokenAmount(activity.price?.amount?.native, {
        symbol: activity.price?.currency?.symbol,
        fallback: '--',
      }),
      From: activity.type === Activity.mint ? '--' : activity.fromAddress,
      To: activity.toAddress || '--',
      Date: new Date((activity.timestamp || 0) * 1000).toISOString(),
      Trace: activity.txHash ? getChainExplorerUrl(marketplaceChain.id, activity.txHash, 'tx') : '',
      Transaction: activity.txHash || '',
    }));

    const documentString = getCSVDocumentString(documentData);
    const fileName = `${formatISO(new Date())}-${address}-activity.csv`;

    downloadFile(documentString, fileName);
  }, [data, address, t, marketplaceChain.id]);

  return (
    <>
      <HStack
        spacing="space.12"
        alignItems="stretch"
        display={{ base: 'flex', lg: 'none' }}
        mb={{ base: 'space.24', lg: 0 }}
      >
        {withRefreshButton && (
          <Flex alignSelf="center">
            <RefreshButton
              onClick={refetch}
              isLoading={isRefetching}
              tooltipLabel={time => t('common:last-updated', { time })}
            />
          </Flex>
        )}
        <FilterDrawer title={t('common:drawers.sort-and-filter.filters.title')}>
          <Stack
            direction="column"
            spacing="space.16"
            border="1px solid"
            borderColor="gray.2"
            borderRadius="radius.12"
            py="space.16"
          >
            {Object.values(ActivityTypeFilters).map((filter, index, self) => (
              <Fragment key={filter}>
                <Checkbox
                  isChecked={selectedFilters.includes(filter)}
                  onChange={() => onFilterToggle(filter)}
                  size="lg"
                  px="space.16"
                >
                  {t(`filters.${filter}.label`)}
                </Checkbox>
                {index + 1 !== self.length && (
                  <Box width="100%" height="1px" display="block" bg="gray.1" />
                )}
              </Fragment>
            ))}
          </Stack>
          {withAttributeFilters && <ActivityAttributeFilters address={address} mt="space.12" />}
        </FilterDrawer>

        <Button
          isDisabled={!data.length}
          onClick={handleDownload}
          variant="secondary"
          rounded="radius.8"
          w="auto"
          mt={0}
        >
          {t('actions.export-csv.label')}
        </Button>
      </HStack>

      {title && (
        <Heading as="h4" size="md" display={{ base: 'none', lg: 'block' }}>
          {title}
        </Heading>
      )}

      <Sticky
        bg="blackBg"
        pt="space.12"
        pb="space.8"
        element="filter-bar"
        zIndex="sticky-layout"
        display={{ base: 'none', lg: 'block' }}
      >
        <Flex alignItems="center" minH={rem(40)}>
          <Stack direction="row" spacing="space.24">
            {withRefreshButton && (
              <RefreshButton
                size="sm"
                onClick={refetch}
                isLoading={isRefetching}
                tooltipLabel={time => t('common:last-updated', { time })}
              />
            )}
            {Object.values(ActivityTypeFilters).map(filter => (
              <Checkbox
                key={filter}
                isChecked={selectedFilters.includes(filter)}
                onChange={() => onFilterToggle(filter)}
              >
                {t(`filters.${filter}.label`)}
              </Checkbox>
            ))}
          </Stack>

          <Stack
            ml="auto"
            spacing="space.20"
            direction="row"
            justifyContent={{ base: 'flex-start', lg: 'flex-end' }}
          >
            <Button
              isDisabled={!data.length}
              variant="link"
              onClick={handleDownload}
              rightIcon={<ExportIcon boxSize={rem(14)} />}
              data-testid="account-activity-export-button"
              fontWeight="500"
            >
              {t('actions.export-csv.label')}
            </Button>
          </Stack>
        </Flex>
        <Divider mt="space.8" mb={0} />
      </Sticky>
    </>
  );
};

export default ActivityFilters;
